import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { portfolioService } from './PortfolioService';

interface CountryContextType {
  countryCode: string;
  setCountryCode: (code: string) => Promise<void>;
}

const CountryContext = createContext<CountryContextType | undefined>(undefined);

const LOCAL_STORAGE_KEY = 'canvasestate_selected_country';

export function CountryProvider({ children }: { children: ReactNode }) {
  const { countryCode: authCountryCode, user } = useAuth();

  // Initialize from localStorage or default to AU
  const [countryCode, setCountryCodeState] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved) {
        return saved;
      }
    } catch (e) {
      console.warn('Error reading country from localStorage:', e);
    }
    return 'AU';
  });

  // Keep state in sync with authenticated country code if it changes
  useEffect(() => {
    if (authCountryCode && authCountryCode !== countryCode) {
      setCountryCodeState(authCountryCode);
      localStorage.setItem(LOCAL_STORAGE_KEY, authCountryCode);
    }
  }, [authCountryCode]);

  // Save to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, countryCode);
    } catch (e) {
      console.warn('Error saving country to localStorage:', e);
    }
  }, [countryCode]);

  const setCountryCode = async (code: string) => {
    setCountryCodeState(code);

    // If user is logged in, also persist to Firestore via API
    if (user) {
      try {
        await portfolioService.updateUserCountry(code);
      } catch (error) {
        console.error('Failed to persist country setting to profile:', error);
      }
    }
  };

  return (
    <CountryContext.Provider value={{ countryCode, setCountryCode }}>
      {children}
    </CountryContext.Provider>
  );
}

export function useCountry() {
  const context = useContext(CountryContext);
  if (context === undefined) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
}
